import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { useOFEFetch } from './useOFE';

export default function OFEVendorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const { data: vendor, loading, error } = useOFEFetch(`/ofe/vendors/${id}`, user);
  const { data: contacts } = useOFEFetch(`/ofe/vendors/${id}/contacts`, user);
  const { data: quality } = useOFEFetch(`/ofe/vendors/${id}/quality`, user, [id]);

  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;
  if (error) return <div className="p-6 text-red-400">Error: {error}</div>;
  if (!vendor) return <div className="p-6 text-gray-400">Vendor not found</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-4">
        <Link to="/ofe/vendors" className="text-gray-400 hover:text-white text-sm">← Vendors</Link>
        <h1 className="text-2xl font-bold text-white">{vendor.vendor_name}</h1>
        <span className="text-gray-400 font-mono text-sm">{vendor.vendor_code}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${vendor.is_active ? 'bg-green-900/40 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
          {vendor.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="flex gap-2">
        {['overview', 'contacts', 'quality', 'production'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm rounded capitalize ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
            <h3 className="text-white font-semibold">Vendor Info</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-gray-400">Type</dt><dd className="text-white">{vendor.vendor_type || '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Oracle ID</dt><dd className="text-white font-mono">{vendor.oracle_vendor_id || '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">MSA Status</dt><dd className="text-white">{vendor.msa_status || '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">MSA Expiry</dt><dd className="text-white">{vendor.msa_expiry || '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Quality Rating</dt><dd className="text-white">{vendor.quality_rating ?? '—'} / 5.0</dd></div>
            </dl>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-3">Equipment Types</h3>
            <div className="flex flex-wrap gap-2">
              {(vendor.equipment_types || []).map(t => (
                <span key={t} className="bg-blue-900/40 text-blue-300 text-xs px-2 py-1 rounded">{t}</span>
              ))}
              {!vendor.equipment_types?.length && <span className="text-gray-400 text-sm">None configured</span>}
            </div>
            {vendor.notes && (
              <div className="mt-3 text-gray-300 text-sm border-t border-gray-700 pt-3">{vendor.notes}</div>
            )}
          </div>
        </div>
      )}

      {tab === 'contacts' && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 text-gray-400">
              <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Phone</th><th className="text-left px-4 py-3">Primary</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(contacts || []).map(c => (
                <tr key={c.id} className="hover:bg-gray-750">
                  <td className="px-4 py-3 text-white">{c.name}</td>
                  <td className="px-4 py-3 text-gray-300">{c.role}</td>
                  <td className="px-4 py-3 text-blue-400">{c.email}</td>
                  <td className="px-4 py-3 text-gray-300">{c.phone}</td>
                  <td className="px-4 py-3">{c.is_primary && <span className="text-green-400 text-xs">✓ Primary</span>}</td>
                </tr>
              ))}
              {!contacts?.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No contacts</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'quality' && (
        <div className="space-y-3">
          {(quality || []).map(q => (
            <div key={q.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${q.severity === 'critical' ? 'bg-red-900/40 text-red-400' : q.severity === 'major' ? 'bg-yellow-900/40 text-yellow-400' : 'bg-gray-700 text-gray-300'}`}>{q.severity}</span>
                <span className="text-white font-semibold text-sm">{q.event_type}</span>
                <span className="text-gray-400 text-xs ml-auto">{q.created_at?.slice(0, 10)}</span>
              </div>
              <p className="text-gray-300 text-sm">{q.description}</p>
              {q.resolution && <p className="text-green-400 text-sm mt-2">Resolution: {q.resolution}</p>}
            </div>
          ))}
          {!quality?.length && <div className="text-gray-400 text-center py-8">No quality events</div>}
        </div>
      )}

      {tab === 'production' && (
        <div>
          <Link to={`/ofe/production/${id}`} className="text-blue-400 hover:text-blue-300">
            View production planner for {vendor.vendor_name} →
          </Link>
        </div>
      )}
    </div>
  );
}
